// 261005 E3-P3b (Lát 4) — nút "Phương án (tốn AI)" ở ③: khớp vấn đề FE đang hiện với CHIP BE cấp (`GET scope localize`), dựng request autoSend. Hàm thuần, KHÔNG import antd/react.
// 🔴 FE KHÔNG tự dựng id chip (B1): chỉ khớp theo trường BE trả (`gate + refs` · `term` · `refs[0]` · `problem`). Không khớp ⇒ `issueIds: []` — câu gửi vẫn mang đủ nội dung (gửi bằng chữ).
import type { LocalizeIssueChip } from '../../../../types/adaptationAssistant';
import type { AssistantRequest } from './assistantIntent';
import { optionsRequest, warningSubject } from './optionsAction';

/** Nút "cả khối" gửi tối đa chừng này id (khớp phần ngữ cảnh gửi AI — D10). */
export const MAX_ISSUE_IDS = 30;
const MAX_NAMES_SHOWN = 8;

export type LocalizeIssueRef =
  /** Một nhóm cảnh báo lượt chạy (gộp theo gate): mọi chip vi phạm cùng `gate`. */
  | { kind: 'violationGate'; gate: string; message: string }
  /** Một vấn đề của cổng AI: khớp `chip.problem`. */
  | { kind: 'gate'; problem: string; where: string }
  /** Cả khối tên chưa ánh xạ. */
  | { kind: 'alias'; terms: string[] }
  /** Các nhân vật chưa có tên. */
  | { kind: 'missingName'; chars: Array<{ id: string; name: string }> };

const uniq = (xs: string[]): string[] => Array.from(new Set(xs));
const norm = (s: string): string => String(s ?? '').trim().replace(/\s+/g, ' ');

/** Id chip khớp (đúng thứ tự BE trả, không trùng, ≤ `MAX_ISSUE_IDS`). `chips` null (chưa/không tải được) ⇒ []. */
export const resolveLocalizeIssueIds = (chips: ReadonlyArray<LocalizeIssueChip> | null | undefined, ref: LocalizeIssueRef): string[] => {
  if (!chips?.length) return [];
  let hit: LocalizeIssueChip[] = [];
  if (ref.kind === 'violationGate') hit = chips.filter((c) => c.kind === 'violation' && c.gate === ref.gate);
  else if (ref.kind === 'gate') { const p = norm(ref.problem); hit = p ? chips.filter((c) => c.kind === 'gate' && norm(c.problem ?? '') === p) : []; }
  else if (ref.kind === 'alias') { const t = new Set(ref.terms.map(norm)); hit = chips.filter((c) => c.kind === 'alias' && t.has(norm(c.term ?? ''))); }
  else { const ids = new Set(ref.chars.map((c) => c.id)); hit = chips.filter((c) => c.kind === 'missingName' && ids.has(c.refs[0])); }
  return uniq(hit.map((c) => c.id)).slice(0, MAX_ISSUE_IDS);
};

const list = (xs: string[]): string => `${xs.slice(0, MAX_NAMES_SHOWN).join(', ')}${xs.length > MAX_NAMES_SHOWN ? ` (+${xs.length - MAX_NAMES_SHOWN})` : ''}`;

/** Chủ đề (cụm danh từ) của một vấn đề — câu "Đưa ra 3 phương án khác cách xử lý <chủ đề>." */
export const localizeSubject = (ref: LocalizeIssueRef): string => {
  if (ref.kind === 'violationGate') return warningSubject('bản địa hoá', { gate: ref.gate, message: ref.message });
  if (ref.kind === 'gate') return `vấn đề cổng AI bản địa hoá: ${ref.problem}${ref.where ? ` (${ref.where})` : ''}`;
  if (ref.kind === 'alias') return `${ref.terms.length} tên nguyên tác chưa ánh xạ (${list(ref.terms)})`;
  return `${ref.chars.length} nhân vật chưa có tên trong phim (${list(ref.chars.map((c) => c.name))})`;
};

/** Request autoSend "3 phương án" cho ③. Chip không khớp ⇒ `issueIds` rỗng (gửi bằng chữ). */
export const localizeOptionsRequest = (chips: ReadonlyArray<LocalizeIssueChip> | null | undefined, ref: LocalizeIssueRef): AssistantRequest =>
  optionsRequest({ stageKey: 'localize', issueIds: resolveLocalizeIssueIds(chips, ref) }, localizeSubject(ref));
